import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { http } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { t } from "@/lib/i18n";
import { COUNTRY_CATALOG } from "@/lib/countries";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { Upload, CheckCircle2, Sparkles, Bell, MapPin, Compass, Trash2, AlertTriangle } from "lucide-react";

export default function Profile() {
  const { user, setUser, lang, loading } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", diploma: "", specialty: "", skills: "", home_country: "", home_region: "", home_city: "" });
  const [saving, setSaving] = useState(false);
  const [cvName, setCvName] = useState("");
  const [parsing, setParsing] = useState(false);

  const regions = useMemo(() => {
    if (!form.home_country) return [];
    const entry = COUNTRY_CATALOG.find((c) => c.name === form.home_country);
    return entry?.regions || [];
  }, [form.home_country]);

  const parseCV = async () => {
    setParsing(true);
    try {
      const { data } = await http.post("/ai/parse-cv");
      setUser(data.user);
      setForm((f) => ({
        ...f,
        name: data.user.name || "",
        diploma: data.user.diploma || "",
        specialty: data.user.specialty || "",
        skills: (data.user.skills || []).join(", "),
      }));
      toast.success(t(lang, "parse_cv_success"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI error");
    } finally { setParsing(false); }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    setForm({
      name: user.name || "",
      diploma: user.diploma || "",
      specialty: user.specialty || "",
      skills: (user.skills || []).join(", "),
      home_country: user.home_country || "",
      home_region: user.home_region || "",
      home_city: user.home_city || "",
    });
    setCvName(user.cv_filename || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, nav, loading]);

  const detectLocation = () => {
    if (!navigator.geolocation) { toast.warning(t(lang, "locate_denied")); return; }
    toast.info(lang === "fr" ? "Détection en cours…" : "Detecting…");
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { data } = await http.get("/geo/reverse", { params: { lat: pos.coords.latitude, lng: pos.coords.longitude } });
          setForm((f) => ({
            ...f,
            home_country: data.country || f.home_country,
            home_region: data.region || f.home_region,
            home_city: data.city || f.home_city,
          }));
          toast.success(lang === "fr" ? "Position détectée — n'oubliez pas d'Enregistrer" : "Location detected — remember to Save");
        } catch {
          toast.warning(lang === "fr" ? "Impossible de résoudre l'adresse" : "Could not resolve address");
        }
      },
      () => toast.warning(t(lang, "locate_denied"), { duration: 4000 }),
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await http.put("/auth/profile", {
        name: form.name,
        diploma: form.diploma,
        specialty: form.specialty,
        skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
        home_country: form.home_country || null,
        home_region: form.home_region || null,
        home_city: form.home_city || null,
      });
      setUser(data);
      toast.success(t(lang, "saved"));
    } finally { setSaving(false); }
  };

  const uploadCv = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await http.post("/auth/cv-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCvName(data.filename);
      toast.success(t(lang, "cv_uploaded"));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-6 py-10">
        <h1 className="font-display text-3xl font-bold tracking-tight text-[#1E3A8A]">{t(lang, "profile_title")}</h1>

        <form onSubmit={save} className="mt-8 grid gap-5 rounded-3xl bg-white border border-slate-200 p-6">
          <div>
            <Label>{t(lang, "name")}</Label>
            <Input data-testid="profile-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" />
          </div>
          <div>
            <Label>{t(lang, "diploma")}</Label>
            <Input data-testid="profile-diploma" placeholder="Master Informatique, BTS Commerce..." value={form.diploma} onChange={(e) => setForm({ ...form, diploma: e.target.value })} className="mt-1.5" />
          </div>
          <div>
            <Label>{t(lang, "specialty")}</Label>
            <Input data-testid="profile-specialty" placeholder="IA & Data, Marketing digital..." value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} className="mt-1.5" />
          </div>
          <div>
            <Label>{t(lang, "skills")}</Label>
            <Textarea data-testid="profile-skills" rows={3} value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} className="mt-1.5" placeholder="Python, React, SQL, ..." />
          </div>

          {/* Location block — used to localize the map, roadmap AI and internship search. */}
          <div className="pt-2 border-t border-slate-100">
            <div className="flex items-center gap-2 mb-2">
              <MapPin className="w-4 h-4 text-[#F97316]" />
              <Label className="text-[#1E3A8A]">{t(lang, "profile_location_title")}</Label>
            </div>
            <p className="text-xs text-slate-500 mb-3">{t(lang, "profile_location_hint")}</p>

            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-slate-500">{t(lang, "home_country")}</Label>
                <Select value={form.home_country || undefined} onValueChange={(v) => setForm({ ...form, home_country: v, home_region: "" })}>
                  <SelectTrigger data-testid="profile-home-country" className="mt-1.5"><SelectValue placeholder={t(lang, "select_country_placeholder")} /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {COUNTRY_CATALOG.map((c) => (
                      <SelectItem key={c.name} value={c.name}>{c.emoji} {c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">{t(lang, "home_region")}</Label>
                {regions.length > 0 ? (
                  <Select value={form.home_region || undefined} onValueChange={(v) => setForm({ ...form, home_region: v })}>
                    <SelectTrigger data-testid="profile-home-region" className="mt-1.5"><SelectValue placeholder={t(lang, "select_region_placeholder")} /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input data-testid="profile-home-region-input" className="mt-1.5" value={form.home_region} onChange={(e) => setForm({ ...form, home_region: e.target.value })} placeholder={t(lang, "select_region_placeholder")} />
                )}
              </div>
              <div>
                <Label className="text-xs text-slate-500">{t(lang, "home_city")}</Label>
                <Input data-testid="profile-home-city" className="mt-1.5" value={form.home_city} onChange={(e) => setForm({ ...form, home_city: e.target.value })} placeholder={lang === "fr" ? "Ex. Montréal" : "e.g. Montreal"} />
              </div>
            </div>
            <Button
              type="button"
              onClick={detectLocation}
              variant="ghost"
              size="sm"
              className="mt-3 text-[#1E3A8A] hover:bg-[#1E3A8A]/5 rounded-full"
              data-testid="profile-detect-location"
            >
              <Compass className="w-4 h-4 mr-1.5" /> {t(lang, "detect_my_location")}
            </Button>
          </div>

          <Button data-testid="profile-save" type="submit" disabled={saving} className="bg-[#1E3A8A] hover:bg-[#172554] rounded-full h-11">
            {saving ? "..." : t(lang, "save")}
          </Button>
        </form>

        {form.home_country && (
          <Link
            to="/internships?near=me"
            data-testid="profile-internships-shortcut"
            className="mt-4 flex items-center justify-between gap-3 rounded-3xl bg-gradient-to-br from-[#1E3A8A] to-[#172554] text-white p-5 hover:shadow-lg transition-shadow"
          >
            <div>
              <p className="font-display font-semibold">{t(lang, "internships_near_me_cta")}</p>
              <p className="text-xs text-white/80 mt-1">
                {form.home_region ? `${form.home_region}, ${form.home_country}` : form.home_country}
              </p>
            </div>
            <Compass className="w-6 h-6" />
          </Link>
        )}

        <div className="mt-6 rounded-3xl bg-white border border-slate-200 p-6">
          <Label>{t(lang, "upload_cv")}</Label>
          <div className="mt-3 flex items-center gap-3">
            <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F97316] hover:bg-[#EA580C] text-white text-sm font-medium">
              <Upload className="w-4 h-4" /> {t(lang, "upload_cv")}
              <input data-testid="cv-input" type="file" accept="application/pdf" onChange={uploadCv} className="hidden" />
            </label>
            {cvName && <span className="text-sm text-slate-700 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-green-600" /> {cvName}</span>}
          </div>
          {cvName && (
            <Button data-testid="parse-cv-btn" type="button" onClick={parseCV} disabled={parsing} className="mt-4 bg-[#1E3A8A] hover:bg-[#172554] rounded-full">
              <Sparkles className="w-4 h-4 mr-1.5" /> {parsing ? t(lang, "analyzing") : t(lang, "parse_cv_ai")}
            </Button>
          )}
        </div>

        {/* SMS Alerts */}
        <SmsPrefsCard />

        {/* Danger zone — irreversible account deletion */}
        <DangerZone />
      </div>
    </Layout>
  );
}

function DangerZone() {
  const { user, lang, logout } = useAuth();
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const expected = "SUPPRIMER";

  if (!user || user.user_type === "admin") return null;

  const doDelete = async () => {
    if (typed !== expected) {
      toast.error(lang === "fr" ? `Tapez ${expected} pour confirmer` : `Type ${expected} to confirm`);
      return;
    }
    setBusy(true);
    try {
      await http.delete("/auth/account");
      toast.success(lang === "fr" ? "Compte supprimé. À bientôt." : "Account deleted. Goodbye.");
      localStorage.removeItem("proxijob_token");
      // Skip server logout — the session is already gone. Force full reload.
      window.location.href = "/";
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-6 rounded-3xl bg-white border-2 border-red-200 p-6" data-testid="danger-zone">
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle className="w-4 h-4 text-red-600" />
        <Label className="text-red-700 font-semibold">
          {lang === "fr" ? "Zone dangereuse" : "Danger zone"}
        </Label>
      </div>
      <p className="text-xs text-slate-600 mb-4 leading-relaxed">
        {lang === "fr"
          ? "Supprimer votre compte est définitif. Vos entreprises, sites web IA, candidatures, favoris et abonnements seront tous effacés."
          : "Deleting your account is permanent. Your companies, AI sites, applications, favorites and subscriptions will all be erased."}
      </p>
      {!confirming ? (
        <Button data-testid="delete-account-btn" onClick={() => setConfirming(true)} variant="outline" className="rounded-full border-red-300 text-red-700 hover:bg-red-50">
          <Trash2 className="w-4 h-4 mr-1.5" /> {lang === "fr" ? "Supprimer mon compte" : "Delete my account"}
        </Button>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-slate-700">
            {lang === "fr" ? `Tapez ` : `Type `}
            <span className="font-mono font-bold text-red-700">{expected}</span>
            {lang === "fr" ? ` pour confirmer :` : ` to confirm:`}
          </p>
          <Input data-testid="delete-confirm-input" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={expected} className="max-w-xs" />
          <div className="flex gap-2 pt-2">
            <Button data-testid="delete-account-confirm-btn" onClick={doDelete} disabled={busy || typed !== expected} className="bg-red-600 hover:bg-red-700 rounded-full">
              <Trash2 className="w-4 h-4 mr-1.5" /> {busy ? "..." : (lang === "fr" ? "Supprimer définitivement" : "Delete permanently")}
            </Button>
            <Button onClick={() => { setConfirming(false); setTyped(""); }} variant="ghost" className="rounded-full">
              {lang === "fr" ? "Annuler" : "Cancel"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function SmsPrefsCard() {
  const { user, setUser, lang } = useAuth();
  const [phone, setPhone] = useState(user?.phone || "");
  const [enabled, setEnabled] = useState(user?.sms_enabled ?? true);
  const [saving, setSaving] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (user) { setPhone(user.phone || ""); setEnabled(user.sms_enabled ?? true); } }, [user]);

  const save = async () => {
    setSaving(true);
    try {
      await http.put("/auth/sms-preferences", { phone, sms_enabled: enabled });
      setUser({ ...user, phone, sms_enabled: enabled });
      toast.success(lang === "fr" ? "Préférences SMS enregistrées" : "SMS preferences saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  return (
    <div className="mt-6 rounded-3xl bg-gradient-to-br from-[#FFF7ED] to-white border border-[#F97316]/30 p-6">
      <div className="flex items-center gap-2 mb-3">
        <Bell className="w-4 h-4 text-[#F97316]" />
        <Label>{lang === "fr" ? "Alertes SMS (jobs d'été / matches carrière)" : "SMS alerts (summer jobs / career matches)"}</Label>
      </div>
      <p className="text-xs text-slate-600 mb-3 leading-relaxed">
        {lang === "fr" ? "Recevez un SMS instantané dès qu'une nouvelle PME locale correspond à votre alerte carrière." : "Get an instant SMS when a new local company matches your career alert."}
      </p>
      <div className="grid md:grid-cols-[1fr_auto_auto] gap-2 items-center">
        <Input data-testid="sms-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+33 6 12 34 56 78" />
        <label className="flex items-center gap-2 text-sm cursor-pointer px-3 py-2">
          <input type="checkbox" data-testid="sms-enabled" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          {lang === "fr" ? "Activé" : "Enabled"}
        </label>
        <Button data-testid="sms-save" onClick={save} disabled={saving} className="bg-[#F97316] hover:bg-[#EA580C] rounded-full">{saving ? "..." : t(lang, "save")}</Button>
      </div>
    </div>
  );
}
